import { ObjApiResponse, ObjBatchApiResponse } from '@/types/obj'
import type { ComponentSchema, ScreenPayload } from '@/types/sduiTypes'
import {
    buildSchemaProperties,
    getDefaultModelValue,
    isFormFieldType,
    isLayoutType,
    resolveComponent,
    resolveTypeFromAlias,
} from '@/features/it-process/application-management/sdui/registry/ComponentRegistry'
import { SDUI_FIELD_BINDING } from '@/features/it-process/application-management/sdui/config/fieldBindingConfig'
import { logger } from '@/utils/logger'
import type { GridElement } from '@/layouts/shells/GridComponentLayout'
import BaseService from '@/lib/BaseService'

export interface ObjResponse extends Record<string, unknown> {
    detId: number
    objId: number
    objVisible: boolean
    objEnabled: boolean
    objToken: string
    objName: string
    objDescription: string
    objType: string
    startColumn: number
    rowNumber: number
    colSpan: number
    rowSpan: number
    events: Array<{ eventName: string; actionRef: string }> | null
}

const SDUI_EVENT_MAP: Record<string, string> = {
    click: 'onClick',
    change: 'onChange',
    blur: 'onBlur',
    input: 'onChange',
    rowclick: 'onRowClick',
}

const toCamelCase = (str: string): string =>
    str
        .trim()
        .split(/\s+/)
        .map((word, i) =>
            i === 0
                ? word.charAt(0).toLowerCase() + word.slice(1)
                : word.charAt(0).toUpperCase() + word.slice(1),
        )
        .join('')

export const objService = {
    getObjList: async (
        dtsId: number,
        baId?: number,
        languageId?: number,
        signal?: AbortSignal,
    ): Promise<ObjResponse[]> => {
        try {
            const params = new URLSearchParams()

            if (baId) params.append('baId', baId.toString())
            if (languageId) params.append('languageId', languageId.toString())
            if (dtsId) params.append('dtsId', dtsId.toString())

            const response = await BaseService.get('/api/screen/det/obj', {
                params,
                signal,
            })

            return transformObjData(response.data)
        } catch (error: unknown) {
            if (error && typeof error === 'object' && 'response' in error) {
                type ErrorResponse = {
                    response?: {
                        status?: number
                        data?: unknown
                    }
                }
                const axiosError = error as ErrorResponse
                const statusCode = axiosError.response?.status

                if (statusCode === 404) {
                    return []
                }
            }
            throw error
        }
    },

    mapToGridElements: (items: ObjResponse[]): GridElement[] =>
        items.map((item) => ({
            id: `${item.detId}-${item.objId}`,
            startCol: item.startColumn,
            span: item.colSpan || 1,
            row: item.rowNumber,
            componentConfig: {
                id: item.objName,
                type: item.objType as any,
                text: item.objToken,
                visible: item.objVisible,
                enabled: item.objEnabled,
            },
        })),

    /**
     * Fetches OBJ lists for multiple screens (dtsIds) in a single round-trip,
     * consolidating what would otherwise be one /api/screen/det/obj call per
     * screen. Returns a map keyed by dtsId so callers can seed a per-dtsId
     * cache (e.g. RTK Query's getObjList entries) from one response.
     */
    getObjListBatch: async (
        dtsIds: number[],
        baId?: number,
        languageId?: number,
        signal?: AbortSignal,
    ): Promise<Record<number, ObjResponse[]>> => {
        if (dtsIds.length === 0) return {}

        let response
        try {
            response = await BaseService.post<ObjBatchApiResponse[]>(
                '/api/screen/det/obj/batch',
                { dtsIds, baId, languageId },
                { signal },
            )
        } catch (error: unknown) {
            // Backend returns 404 when none of the dtsIds have OBJ items (e.g.
            // no BA resolved for the user). Mirror getObjList's 404 -> [] so the
            // query settles as empty instead of erroring and being re-fetched.
            const status = (error as { response?: { status?: number } })
                ?.response?.status
            if (status === 404) return {}
            throw error
        }

        const result: Record<number, ObjResponse[]> = {}
        for (const entry of response.data) {
            // objItems is null for dtsIds with no data — leave those out so the
            // per-tab getObjList call handles them rather than crashing here.
            if (entry.objItems == null) continue
            result[entry.dtsId] = transformObjData(entry.objItems)
        }
        return result
    },
}

export const transformObjData = (
    apiData: ObjApiResponse[] | string,
): ObjResponse[] => {
    let result: ObjApiResponse[]
    if (typeof apiData === 'string') {
        try {
            result = JSON.parse(apiData)
        } catch (error) {
            logger.error('Failed to parse API data as JSON:', error)
            return []
        }
    } else {
        result = apiData
    }

    return result.map((item) => ({
        detId: item.DETId,
        objId: item.OBJId,
        objVisible: item.ObjVisible,
        objEnabled: item.ObjEnable,
        objToken: item.OBJToken,
        objName: item.OBJName,
        objDescription: item.OBJDescription,
        objType: item.OBJType,
        startColumn: item.StartColumn,
        rowNumber: item.RowNumber,
        colSpan: item.ColSpan,
        rowSpan: item.RowSpan,
        events: item.Events
            ? item.Events.map((e) => ({
                  eventName: e.EventName,
                  actionRef: e.ActionRef,
              }))
            : null,
    }))
}

/**
 * Build a schema's layout. Containers (e.g. grids) that arrive without any
 * usable position/size fall back to auto sizing (full width, content height)
 * at render time.
 */
const buildSchemaLayout = (
    item: ObjResponse,
    sduiType: string,
): ComponentSchema['layout'] => {
    const hasNoLayout =
        !item.startColumn && !item.rowNumber && !item.colSpan && !item.rowSpan
    const isAutoLayout = isLayoutType(sduiType) && hasNoLayout
    return {
        startCol: item.startColumn,
        colSpan: item.colSpan || 1,
        row: item.rowNumber,
        rowSpan: item.rowSpan || 1,
        ...(isAutoLayout && { auto: true }),
    }
}

/**
 * Map OBJ events onto SDUI event keys, or undefined when none resolve.
 */
const buildSchemaEvents = (
    objEvents: ObjResponse['events'],
): ComponentSchema['events'] | undefined => {
    if (!objEvents || objEvents.length === 0) return undefined
    const events: ComponentSchema['events'] = {}
    for (const { eventName, actionRef } of objEvents) {
        const key = eventName.toLowerCase()
        // Resolve with an `on`-prefix fallback so both `onrowclick` and
        // `rowclick` (and `onclick`/`click`, etc.) map correctly.
        const sduiEvent =
            SDUI_EVENT_MAP[key] ?? SDUI_EVENT_MAP[key.replace(/^on/, '')]
        if (sduiEvent) {
            ;(events as Record<string, { actionRef: string }>)[sduiEvent] = {
                actionRef,
            }
        }
    }
    return Object.keys(events).length > 0 ? events : undefined
}

export const buildUISchema = (items: ObjResponse[]): ComponentSchema[] => {
    const schemas: ComponentSchema[] = []

    for (const item of items) {
        if (!item.objVisible) continue

        const sduiType =
            resolveTypeFromAlias(item.objType) ?? item.objType.toLowerCase()

        if (!resolveComponent(sduiType)) {
            logger.warn(
                `[SDUI] Unknown component type "${item.objType}" for OBJ "${item.objName}" � skipping`,
            )
            continue
        }

        const schema: ComponentSchema = {
            id: item.objName,
            type: sduiType,
            layout: buildSchemaLayout(item, sduiType),
        }

        schema.properties = buildSchemaProperties(
            sduiType,
            item.objToken,
            item.objDescription,
            item.objEnabled,
        )

        // Data binding for form fields
        if (isFormFieldType(sduiType) && item.objToken) {
            schema.dataBinding = { field: toCamelCase(item.objToken) }
        }

        // Events
        const events = buildSchemaEvents(item.events)
        if (events) {
            schema.events = events
        }

        schemas.push(schema)
    }

    return schemas
}

/**
 * Whether a schema's bound field is seeded from the selected row, i.e. its
 * OBJName has an entry in SDUI_FIELD_BINDING. Binding-driven fields are the ones
 * that re-seed when the selected ancestor row changes (unless the user edited them).
 */
export const isBindingDrivenField = (schema: ComponentSchema): boolean =>
    Boolean(schema.dataBinding?.field) && schema.id in SDUI_FIELD_BINDING

/**
 * Resolve the seeded value for a single bound schema. Precedence:
 * selectedRow[boundSource] > dataModelDefaults[field] > type default.
 * Shared by assembleScreenPayload and the sdui cache slice so seed logic lives
 * in exactly one place.
 */
export const computeSeededValue = (
    schema: ComponentSchema,
    selectedRow?: Record<string, unknown> | null,
    dataModelDefaults?: Record<string, unknown>,
): unknown => {
    const typeDefault = getDefaultModelValue(schema.type)
    const sourceField = SDUI_FIELD_BINDING[schema.id]
    const fromRow = sourceField ? selectedRow?.[sourceField] : undefined
    const fromDefaults = schema.dataBinding?.field
        ? dataModelDefaults?.[schema.dataBinding.field]
        : undefined
    return fromRow ?? fromDefaults ?? typeDefault
}

/**
 * Build a flat dataModel (field -> seeded value) for every bound field in the
 * uiSchema, applying computeSeededValue's precedence.
 */
export const computeSeededDataModel = (
    uiSchema: ComponentSchema[],
    options?: {
        dataModelDefaults?: Record<string, unknown>
        selectedRow?: Record<string, unknown> | null
    },
): Record<string, unknown> => {
    const dataModel: Record<string, unknown> = {}
    for (const schema of uiSchema) {
        if (schema.dataBinding?.field) {
            dataModel[schema.dataBinding.field] = computeSeededValue(
                schema,
                options?.selectedRow,
                options?.dataModelDefaults,
            )
        }
    }
    return dataModel
}

export const assembleScreenPayload = (
    items: ObjResponse[],
    dtsId: number,
    options?: {
        actionDomain?: string
        dataModelDefaults?: Record<string, unknown>
        /** Selected row (from an ancestor tab) used to prefill bound fields via SDUI_FIELD_BINDING. */
        selectedRow?: Record<string, unknown> | null
    },
): ScreenPayload => {
    const uiSchema = buildUISchema(items)

    const dataModel = computeSeededDataModel(uiSchema, {
        dataModelDefaults: options?.dataModelDefaults,
        selectedRow: options?.selectedRow,
    })

    return {
        metadata: {
            version: '1.0.0',
            screenId: `det-${dtsId}`,
            layoutType: 'grid',
            columns: 12,
            cacheKey: `det-${dtsId}-objs`,
            ...(options?.actionDomain && {
                actionDomain: options.actionDomain,
            }),
        },
        uiSchema,
        dataModel,
    }
}

export default objService
